import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import {
	TableStatus,
	useGetCurrentMealPeriodLazyQuery,
	useGetDailyMenuCategoryLazyQuery,
	useGetOrderQuery,
} from 'generated/graphql';
import {
	buildPatronReceiptPath,
	getLatestOrderBillId,
} from 'helpers/patronReceiptNavigation';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

type PatronOrderGuardProps = {
	children: React.ReactNode;
};

export const PatronOrderGuard = ({ children }: PatronOrderGuardProps) => {
	const history = useHistory();
	const { outletID, tableID, orderID, menuID } = useParams<
		Record<string, string>
	>();
	const [allowed, setAllowed] = useState(false);
	const hasResolvedRef = useRef(false);
	const pendingBillIdRef = useRef<string | null>(null);
	const mealPeriodIdRef = useRef<string | null>(null);

	const { loading, data: { getOrder } = { getOrder: [] } } = useGetOrderQuery({
		variables: { ID: orderID },
		fetchPolicy: 'network-only',
		skip: !orderID,
	});

	const navigateToReceipt = (resolvedMenuID: string) => {
		const billID = pendingBillIdRef.current;

		if (!billID || !outletID || !tableID || !orderID) {
			setAllowed(true);
			return;
		}

		history.replace(
			buildPatronReceiptPath({
				outletID,
				tableID,
				orderID,
				menuID: resolvedMenuID,
				billID,
			}),
		);
	};

	const [loadCategory] = useGetDailyMenuCategoryLazyQuery({
		fetchPolicy: 'network-only',
		onCompleted: ({ getOutletDailyMenuCategory }) => {
			const resolvedMenuID =
				menuID || getOutletDailyMenuCategory?.[0]?.ID || 'receipt';
			navigateToReceipt(resolvedMenuID);
		},
		onError: () => {
			navigateToReceipt(menuID || 'receipt');
		},
	});

	const [loadCurrentMealPeriod] = useGetCurrentMealPeriodLazyQuery({
		fetchPolicy: 'network-only',
		onCompleted: ({ getCurrentMealPeriod }) => {
			mealPeriodIdRef.current = getCurrentMealPeriod?.ID || null;
			loadCategory({
				variables: {
					mealPeriodID: mealPeriodIdRef.current,
					outletID,
				},
			});
		},
		onError: () => {
			navigateToReceipt(menuID || 'receipt');
		},
	});

	const redirectToReceiptIfNeeded = (billID: string) => {
		pendingBillIdRef.current = billID;

		if (menuID) {
			navigateToReceipt(menuID);
			return;
		}

		loadCurrentMealPeriod({
			variables: {
				outletID,
			},
		});
	};

	useEffect(() => {
		if (loading || hasResolvedRef.current) {
			return;
		}

		hasResolvedRef.current = true;

		const order = getOrder?.[0];

		if (!order) {
			setAllowed(true);
			return;
		}

		if (order.table?.status === TableStatus.Available) {
			history.replace('/menu/patron-app/invalid-qr');
			return;
		}

		const billID = getLatestOrderBillId(order.bill);

		if (billID) {
			redirectToReceiptIfNeeded(billID);
			return;
		}

		setAllowed(true);
	}, [loading, getOrder, history, menuID, orderID, outletID]);

	if (loading || !allowed) {
		return <Loading />;
	}

	return <>{children}</>;
};
